import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '@/lib/services/dashboard-api';
import { SharedStream } from '@/lib/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Eye,
  ArrowRight,
  Video,
  Users,
  Clock,
  RefreshCw,
  Activity,
  AlertCircle,
  CheckCircle2,
  Radio
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useDurableObjectChannel } from '@/hooks/use-durable-object-channel';
import { MediaImage } from '@/components/ui/media-image';

interface LiveMonitoringWidgetProps {
  onViewMore?: () => void;
  maxStreams?: number;
  refreshInterval?: number; // in milliseconds
}

interface StreamWithAnalytics extends SharedStream {
  liveViewerCount?: number;
  healthStatus?: 'healthy' | 'warning' | 'error';
}

export function LiveMonitoringWidget({
  onViewMore,
  maxStreams = 5,
  refreshInterval = 30000 // 30 seconds default (deprecated - kept for backward compatibility)
}: LiveMonitoringWidgetProps) {
  const navigate = useNavigate();
  const [streams, setStreams] = useState<StreamWithAnalytics[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchLiveStreams = useCallback(async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setError(null);

      const liveStreams = await dashboardApi.getLiveStreams(maxStreams);

      // Fetch analytics for each stream to get viewer counts
      const streamsWithAnalytics: StreamWithAnalytics[] = await Promise.all(
        liveStreams.map(async (stream) => {
          try {
            const analytics = await dashboardApi.getStreamAnalytics(stream.id);
            return {
              ...stream,
              liveViewerCount: analytics?.viewerCount ?? stream.viewerCount ?? 0,
              healthStatus: determineHealthStatus(stream, analytics?.viewerCount ?? 0)
            };
          } catch {
            return {
              ...stream,
              liveViewerCount: stream.viewerCount ?? 0,
              healthStatus: 'warning' as const
            };
          }
        })
      );

      setStreams(streamsWithAnalytics);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching live streams:', err);
      setError('Failed to load live streams');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [maxStreams]);

  // Determine health status based on stream data
  const determineHealthStatus = (
    stream: SharedStream,
    viewerCount: number
  ): 'healthy' | 'warning' | 'error' => {
    // If stream has been live for more than 5 minutes with 0 viewers, might be an issue
    if (stream.actualStart) {
      const startTime = new Date(stream.actualStart);
      const now = new Date();
      const minutesLive = (now.getTime() - startTime.getTime()) / (1000 * 60);

      if (minutesLive > 5 && viewerCount === 0) {
        return 'warning';
      }
    }

    return 'healthy';
  };

  // Subscribe to real-time live monitoring updates via Durable Objects
  const handleMessage = useCallback((message: any) => {
    if (message.type === 'live_streams_update' && message.liveStreams) {
      setStreams(message.liveStreams);
      setLastUpdated(new Date());
    } else if (message.type === 'stream_health_update') {
      // Update individual stream health
      setStreams(prev => prev.map(stream =>
        stream.id === message.streamId
          ? { ...stream, liveViewerCount: message.viewerCount, healthStatus: message.healthStatus }
          : stream
      ));
    }
  }, []);

  const { isConnected } = useDurableObjectChannel({
    channel: 'admin:live-monitoring',
    enabled: true,
    onMessage: handleMessage,
  });

  useEffect(() => {
    // Initial fetch
    fetchLiveStreams();
  }, [fetchLiveStreams]);

  const handleManualRefresh = () => {
    fetchLiveStreams(false);
  };

  const getHealthIcon = (status?: 'healthy' | 'warning' | 'error') => {
    switch (status) {
      case 'healthy':
        return <CheckCircle2 className="h-3 w-3 text-green-500" />;
      case 'warning':
        return <AlertCircle className="h-3 w-3 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="h-3 w-3 text-red-500" />;
      default:
        return <Activity className="h-3 w-3 text-muted-foreground" />;
    }
  };

  const formatDuration = (startTime?: string) => {
    if (!startTime) return 'N/A';
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Live Stream Monitoring
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(Math.min(maxStreams, 3))].map((_, i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse p-3 rounded-lg border">
                <div className="w-12 h-12 bg-gray-200 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Live Stream Monitoring
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchLiveStreams()}
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Live Stream Monitoring
            {streams.length > 0 && (
              <Badge variant="destructive" className="ml-2 animate-pulse">
                <Radio className="h-3 w-3 mr-1" />
                {streams.length} Live
              </Badge>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        {lastUpdated && (
          <p className="text-xs text-muted-foreground mt-1">
            Last updated: {formatDistanceToNow(lastUpdated, { addSuffix: true })}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {streams.length === 0 ? (
          <div className="text-center py-8">
            <Video className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No Live Streams</h3>
            <p className="text-sm text-muted-foreground mb-4">
              There are no active streams to monitor at the moment.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {streams.map((stream) => (
              <button
                key={stream.id}
                onClick={() => navigate(`/streams/${stream.id}`)}
                className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left w-full"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center relative">
                  {stream.thumbnailUrl ? (
                    <MediaImage
                      src={stream.thumbnailUrl}
                      alt={stream.title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <Video className="h-6 w-6 text-red-600" />
                  )}
                  {/* Live indicator */}
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium truncate">{stream.title}</p>
                    <Badge
                      variant="destructive"
                      className="text-xs px-1.5 py-0.5"
                    >
                      LIVE
                    </Badge>
                    {getHealthIcon(stream.healthStatus)}
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {/* Viewer count */}
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span className="font-medium">
                        {stream.liveViewerCount ?? stream.viewerCount ?? 0}
                      </span>
                      <span>viewers</span>
                    </div>

                    {/* Duration */}
                    {stream.actualStart && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDuration(stream.actualStart)}</span>
                      </div>
                    )}

                    {/* Creator */}
                    {stream.creator && (
                      <div className="flex items-center gap-1 truncate">
                        <span className="truncate">{stream.creator.name}</span>
                      </div>
                    )}
                  </div>
                </div>

                <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
            ))}

            {onViewMore && (
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onViewMore}
                  className="w-full justify-between"
                >
                  View All Live Streams
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
